/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  onConnect,
  reaction,
  reatomArray,
  spawn,
  withAssign,
} from '@reatom/framework'

import { Array, AsyncStateN, Result } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import {
  pairRegistry,
  reatomPairInfoBulkSubscription,
  type PairModel,
} from '@root/entities/pair'
import { blacklistCurrenciesModel } from '@root/features/blacklist/blacklist-currencies/atoms'
import { reatomQuickBuyQuoteBulkSubscription } from '@root/features/quote/atoms'
import { subscribeOnPairEventsAction } from '@root/features/realtime/atoms'
import { processPairEventsUpdateAction } from '@root/features/realtime/utils'
import { type Error } from '@root/shared'
import { SKIP_EXPLORE_CLIENT_FILTERS } from '@root/shared/environment'
import { dateByMinuteAtom, reatomResource } from '@root/shared/lib'

import { ExploreFilter } from '../model'

import type { ExploreFiltersAtom } from './reatom-explore-filters'
import type { Atom } from '@reatom/framework'
import type { Order } from '@blade/std-lib'
import type { Ctx, CtxSpy } from '@reatom/framework'
import type { Network } from '@root/entities/network'
import type { PairMetrics } from '@root/entities/pair'
import type {
  PairEventPayload,
  PairEventType,
} from '@root/features/realtime/atoms'
import type { PairsUpdatePayload } from '@root/features/realtime/utils'

const NULL_DATE = new Date()

const MAX_COUNT = 20

/**
 * How many of a column's pools have their trades streamed.
 *
 * The column holds twenty and shows about four. A busy pool sends a couple of
 * frames a second on its own, so this is drawn at what a reader can plausibly
 * scroll to rather than at the whole list.
 */
const TRADE_STREAM_COUNT = 10

interface PairEventsCommonArgs<Event extends PairEventType> {
  compare?: (ctx: Ctx) => Order.Order<PairModel> | undefined
  filter?: ExploreFiltersAtom
  /**
   * Whether to act on what the socket pushes.
   *
   * The guard is on the handler rather than the subscription: dropping a
   * message is one line, while tearing a subscription up and down as a
   * setting flips is a lifecycle to get wrong. The socket keeps talking and
   * this list stops listening, which is all that was wanted.
   */
  live?: Atom<boolean>
  event: Event
  period: PairMetrics.MetricsSpan
  loadItems: (
    ctx: CtxSpy,
    args: {
      network: Network.Network
      limit: number
      filter?: ExploreFilter.ExploreFilter | undefined
      period: PairMetrics.MetricsSpan
    },
  ) => Promise<Result.Result<Array<PairModel>, Error.UnknownError>>
}

export const reatomPairEventsCommon = <Event extends PairEventType>({
  loadItems,
  event,
  filter,
  compare,
  period,
  live,
}: PairEventsCommonArgs<Event>) => {
  const bufferAtom = atom<Array<PairsUpdatePayload>>([], 'bufferAtom')
  const buffedNewCountAtom = atom((ctx) => {
    const buffer = ctx.spy(bufferAtom)
    return buffer.filter((it) => it.updateType === 'append').length
  })
  const pausedAtom = atom(false, 'pausedAtom')

  const pairInfoSubscriptionModel = reatomPairInfoBulkSubscription({
    name: `explore-events-${event}`,
  })

  /**
   * The trade stream for the pools at the top of the column.
   *
   * A card's figures otherwise only move when the server has rebuilt it out
   * of ClickHouse, which it does on a poll: measured on staging, a watched
   * pool's card arrives about every three seconds and a card's text changed
   * five times in eighty. Counting trades as they land is what makes the
   * numbers move with the market, and the rebuilt card stays the correction.
   */
  const tradesSubscriptionModel = reatomPairInfoBulkSubscription({
    name: `explore-trades-${event}`,
    trigger: (pair) => pair.tradesSubscriptionTrigger,
    limit: TRADE_STREAM_COUNT,
  })
  const quickBuyQuoteSubscriptionBulkModel =
    reatomQuickBuyQuoteBulkSubscription({
      subscriberKey: `explore-events-${event}`,
      section: 'exploreFeeds',
      autoSubscribe: false,
    })

  const pairsStateAtom = reatomResource(
    async (ctx) => {
      bufferAtom(ctx, [])
      const network = ctx.spy(selectedNetworkAtom)
      const filters = filter ? ctx.spy(filter) : undefined

      // Tried waiting here for the socket to hand the list over and fetching
      // only if it did not: the await broke the resource's own scheduling and
      // the column was left with neither, no request and no list. The socket
      // still fills it the moment its snapshot lands — this just no longer
      // tries to be clever about the fetch behind it.
      // Will be scheduled in usages because we need spy
      // eslint-disable-next-line @reatom/async-rule
      const result = await loadItems(ctx, {
        network,
        limit: MAX_COUNT,
        filter: filters,
        period,
      })

      return result.pipe(Result.map((it) => reatomArray(it)))
    },
    {
      retry: {
        enabled: true,
      },
      reconnectStrategy: 'none',
    },
  )

  const flushBufferAction = action((ctx) => {
    const pairsState = ctx.get(pairsStateAtom)

    if (AsyncStateN.isSuccess(pairsState)) {
      const buffer = ctx.get(bufferAtom)
      for (const update of buffer) {
        processPairEventsUpdateAction(ctx, {
          currentPairsAtom: pairsState.value,
          update,
          maxCount: MAX_COUNT,
        })
      }
      bufferAtom(ctx, [])
    }
  })

  const pausedReaction = reaction((ctx) => {
    const paused = ctx.spy(pausedAtom)

    if (paused) {
      return
    }

    flushBufferAction(ctx)
  }, 'pausedReaction')

  const handlePairEvents = action((ctx, updateDto: PairEventPayload) => {
    // Not the list on screen: the socket pushes one ranking, and this column
    // is showing another.
    if (live && !ctx.get(live)) {
      return
    }

    // handled in pair-update
    if (updateDto.updateType === 'update-existing') {
      return
    }

    if (updateDto.updateType === 'replace') {
      const hasFilter = filter ? ctx.get(filter.anyFilterApplied) : false
      if (hasFilter && !SKIP_EXPLORE_CLIENT_FILTERS) {
        return
      }
    }

    const pairsState = ctx.get(pairsStateAtom)
    const paused = ctx.get(pausedAtom)

    const exploreFilter = filter ? ctx.get(filter) : undefined
    const update: PairsUpdatePayload = {
      pairs: updateDto.pairs
        .map((it) => pairRegistry.resolveFromDto(ctx, it))
        .filter((it) =>
          exploreFilter
            ? ExploreFilter.isPairSatisfiesExploreFilter(
                exploreFilter,
                period,
                new Date(),
              )(ctx, it)
            : true,
        ),
      updateType: updateDto.updateType,
    }

    if (update.pairs.length === 0) {
      return
    }

    // The socket hands a list over the moment we subscribe, and that beats
    // the HTTP page by a second or more: the ws ticket is ready at ~2.1s
    // while the pages come back between 3.7s and 7.1s. Taken as the list
    // when there is none yet, so a column fills from whichever arrives
    // first and the fetch behind it stays as the fallback.
    if (update.updateType === 'replace' && !AsyncStateN.isSuccess(pairsState)) {
      pairsStateAtom.setSuccess(ctx, reatomArray(update.pairs))
      return
    }

    const updateCurrentItems = () => {
      if (!AsyncStateN.isSuccess(pairsState)) {
        return
      }
      if (paused) {
        return
      }
      processPairEventsUpdateAction(ctx, {
        currentPairsAtom: pairsState.value,
        update: update,
        maxCount: MAX_COUNT,
      })
    }
    updateCurrentItems()

    if (!paused) {
      return
    }

    if (update.updateType === 'replace') {
      bufferAtom(ctx, [update])
      return
    }

    if (update.updateType === 'append') {
      const currentBuffer = ctx.get(bufferAtom)
      bufferAtom(ctx, Array.take(MAX_COUNT)([...currentBuffer, update]))
      return
    }
  }, 'handlePairEvents')

  const filtersReferenceDateAtom = atom((ctx) => {
    if (!filter) {
      return NULL_DATE
    }

    const exploreFilter = ctx.spy(filter)
    if (!exploreFilter.minAge && !exploreFilter.maxAge) {
      return NULL_DATE
    }
    const date = ctx.spy(dateByMinuteAtom)
    return date
  })

  const filteredPairsStateAtom = atom((ctx) => {
    const pairsState = ctx
      .spy(pairsStateAtom)
      .pipe(AsyncStateN.map((pairsAtom) => ctx.spy(pairsAtom)))

    if (!filter) {
      return pairsState
    }

    const _referenceDate = ctx.spy(filtersReferenceDateAtom)
    const date = new Date()

    const exploreFilter = ctx.spy(filter)

    return pairsState.pipe(
      AsyncStateN.map((pairs) =>
        pairs.filter((it) =>
          ExploreFilter.isPairSatisfiesExploreFilter(
            exploreFilter,
            period,
            date,
          )(ctx, it),
        ),
      ),
    )
  })

  const sortedPairsStateAtom = atom((ctx) => {
    const pairsState = ctx.spy(filteredPairsStateAtom)

    const compare_ = compare ? compare(ctx) : undefined

    const sortedPairsState = pairsState.pipe(
      AsyncStateN.map((pairs) => {
        if (!compare_) {
          return pairs
        }
        return Array.sort(compare_)(pairs)
      }),
    )
    return sortedPairsState
  })

  const resolvedPairsStateAtom = atom((ctx) => {
    const pairsState = ctx.spy(sortedPairsStateAtom)
    const hiddenAddresses = ctx.spy(
      blacklistCurrenciesModel.blacklistedAddresses,
    )
    return pairsState.pipe(
      AsyncStateN.map((pairs) => {
        const withMigrated = pairs.map((it) => {
          return ctx.spy(it.migratedPair) ?? it
        })

        if (hiddenAddresses.size === 0) {
          return withMigrated
        }
        return withMigrated.filter(
          (it) => !hiddenAddresses.has(it.quote.address),
        )
      }),
    )
  }, 'resolvedPairsStateAtom')

  const initializeAction = action((ctx) => {
    const state = ctx.get(resolvedPairsStateAtom)
    if (AsyncStateN.isSuccess(state)) {
      return
    }
    // After user has seen explore, always listen to pair event updates
    spawn(ctx, (ctx) => {
      subscribeOnPairEventsAction(ctx, {
        event,
        handler: handlePairEvents,
        maxCount: MAX_COUNT,
        filtersAtom: filter,
      })
    })
  })

  onConnect(resolvedPairsStateAtom, (ctx) => {
    initializeAction(ctx)

    const pausedSub = pausedReaction(ctx)

    const unsubPairInfoReaction = resolvedPairsStateAtom.onChange(
      (ctx, pairsState) => {
        if (AsyncStateN.isError(pairsState)) {
          pairInfoSubscriptionModel.unsubscribeAll(ctx)
          tradesSubscriptionModel.unsubscribeAll(ctx)
          quickBuyQuoteSubscriptionBulkModel.unsubscribeAll(ctx)
          return
        }

        if (AsyncStateN.isSuccess(pairsState)) {
          pairInfoSubscriptionModel.processPairs(ctx, pairsState.value)
          tradesSubscriptionModel.processPairs(ctx, pairsState.value)
          quickBuyQuoteSubscriptionBulkModel.processPairs(ctx, pairsState.value)
          return
        }
      },
    )

    return () => {
      pausedSub.unsubscribe()
      unsubPairInfoReaction()
      pairInfoSubscriptionModel.unsubscribeAll(ctx)
      tradesSubscriptionModel.unsubscribeAll(ctx)
      quickBuyQuoteSubscriptionBulkModel.unsubscribeAll(ctx)
      pausedAtom(ctx, false)
    }
  })

  return resolvedPairsStateAtom.pipe(
    withAssign(() => ({
      resolvedPairsStateAtom,
      load: pairsStateAtom.run,
      paused: pausedAtom,
      refresh: pairsStateAtom.refresh,
      buffedCount: buffedNewCountAtom,
      flushBuffer: flushBufferAction,
      period,
    })),
  )
}
